import {
  everyTrue,
  hasSameClearingAccount,
  isAddressInMoveOut,
  isAddressInRelocation,
  isContractStatus,
  isMoveOutOnlineNotPossible,
  isRelocationChangeOnlineNotPossible,
} from '@mwe/utils';
import { ChangeDetectionStrategy, Component, computed, inject, input, model, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { PRODUCTS_DETAILS_ROUTE, PRODUCTS_ROUTE } from '@mwe/constants';
import { ClearingAccount, ContractStatusEnum, Product, Sparte, UnpaidInvoices } from '@mwe/models';
import { FindPaymentSolutionLogic, ProductDetailsStateService, ProductsWhiteListLogic, RelocationService } from '@mwe/services';
import { BadgeType } from '../../badge/badge.model';
import { BasicCardClickableComponent } from '../../cards/card-clickable/basic-card-clickable.component';
import { ProductIconComponent } from '../../product-icon/product-icon.component';
import { BadgeComponent } from '../../badge/badge/badge.component';
import { AsyncPipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { CorrelatedCompanyReferencePipe, TranslateCategoryPipe, TranslateTariffClassPipe } from '@mwe/ui/pipes';

@Component({
  selector: 'mwe-product-card',
  templateUrl: './products-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BasicCardClickableComponent,
    ProductIconComponent,
    BadgeComponent,
    NgClass,
    NgStyle,
    NgTemplateOutlet,
    TranslateDirective,
    AsyncPipe,
    TranslatePipe,
    TranslateCategoryPipe,
    CorrelatedCompanyReferencePipe,
    TranslateTariffClassPipe,
  ],
})
export class ProductsCardComponent implements OnInit {
  iconExtendedStyle = model<string>();
  product = model<Product>();
  groupProducts = input<Product[]>(undefined);
  state = input<'details' | 'list'>(undefined);
  showCompanyCorrelation = input<boolean>(undefined);
  checkIfFernwaermeIsActive = input<boolean>(false);
  unpaidInvoices = input<UnpaidInvoices>(undefined);
  useClickableCardStyle = input<boolean>(false);
  hasContractEnded = computed<boolean>(() => isContractStatus(this.product().vertragsStatus, ContractStatusEnum.Beendet));
  isActiveBadgeVisible = computed<boolean>(() => {
    return isContractStatus(this.product().vertragsStatus, ContractStatusEnum.Aktiv) && this.state() === 'list';
  });
  contractEndedBadgeTyp = computed<BadgeType>(() => (this.state() === 'details' ? 'warning' : 'neutral'));

  isDetailsState = computed(() => this.state() === 'details');
  categoryIcon = computed(() => {
    const category = this.product().category.toLowerCase();
    if (this.iconExtendedStyle() && this.iconExtendedStyle().length > 0) {
      return `${category}-${this.iconExtendedStyle()}`;
    }

    return category;
  });
  onlineNotPossibleWarningLabel: string;
  buttonIsFocused = false;
  isClickable = computed(() => this.isActive() && this.isDetailsState());
  private isRelocationOnlineNotPossible = false;
  private isMoveOutOnlineNotPossible = false;
  private router = inject(Router);
  private productDetailsStateService = inject(ProductDetailsStateService);
  private productsWhiteList = inject(ProductsWhiteListLogic);
  isActive = computed(() => this.productsWhiteList.isInWhiteList(this.product()?.category));
  private relocationService = inject(RelocationService);
  private findPaymentSolutionLogic = inject(FindPaymentSolutionLogic);

  async ngOnInit(): Promise<void> {
    await this.setNoOnlineCategory();

    if (!this.isActive && !this.iconExtendedStyle()) {
      this.iconExtendedStyle.set('grau');
    }
  }

  showProductDetails(event): void {
    event.preventDefault();
    this.productDetailsStateService.product = this.product();
    this.productDetailsStateService.groupProducts = this.groupProducts();
    this.router.navigateByUrl(`/${PRODUCTS_ROUTE}/${PRODUCTS_DETAILS_ROUTE}`);
  }

  showUnpaidInvoices(): void {
    const categories = this.groupProducts()
      .filter(gp => hasSameClearingAccount(gp, this.product()))
      .map(gp => gp.category);
    const clearingAccount: ClearingAccount = {
      accountId: this.product().accountNumber,
      systemId: this.product().systemId,
      businessPartnerId: this.product().businessPartnerNumber,
      unpaidInvoices: this.unpaidInvoices(),
    };
    this.findPaymentSolutionLogic.navigateToUnpaidInvoices(categories as Sparte[], clearingAccount, this.product().address);
  }

  async setNoOnlineCategory(): Promise<void> {
    const isRelocation = isAddressInRelocation(this.product().address);
    if (everyTrue(isRelocation, isRelocationChangeOnlineNotPossible(this.product().category))) {
      try {
        const relocationData = await this.relocationService.getParsedRelocationSummaryDetailsFor(this.product().address.addressGroup.id);
        this.isRelocationOnlineNotPossible = relocationData.noOnlineCategories.includes(this.product().category);
      } catch {
        // REVIEW !!! is this the correct default behaviour ??
        this.isRelocationOnlineNotPossible = true;
      }
    }

    this.isMoveOutOnlineNotPossible = isAddressInMoveOut(this.product().address) && isMoveOutOnlineNotPossible(this.product().category);

    if (this.isMoveOutOnlineNotPossible || this.isRelocationOnlineNotPossible) {
      this.iconExtendedStyle.set('');

      this.product.update(old => ({
        ...old,
        isAddressDetailsLinkOnlineNotPossible: true,
      }));
      this.onlineNotPossibleWarningLabel = this.isMoveOutOnlineNotPossible
        ? 'products.card.moveOutActive'
        : 'products.card.relocationActive';
    }
  }
}
